/**
 * dotbox.ai — Design Tokens (iter66)
 *
 * Single source of truth for text sizes, spacing scales, color aliases, and
 * shared component patterns. Import these instead of copy-pasting className
 * strings across components.
 *
 * Usage:
 *   import { TEXT, COLOR, LAYOUT, PATTERN } from '@/design_tokens';
 *   <h1 className={TEXT.h1}>Data Sources</h1>
 *   <div className={PATTERN.card}>...</div>
 *
 * Rules:
 *   - Never hardcode `text-2xl`, `bg-[#0F1730]`, `text-slate-100` etc. in
 *     new components — import a token instead.
 *   - If you need a new size / color / pattern, add it here first.
 *   - PALETTE colors are the canonical dotbox brand hex values — do NOT drift.
 *
 * Migration guide:
 *   - Any page still using ad-hoc `<h1 className="font-bold text-white">` or
 *     `<h1 className="text-2xl font-bold text-slate-100">` should adopt the
 *     `<PageHeader />` component in `components/common/PageHeader.jsx`, which
 *     consumes these tokens.
 */

// ============================================================
// Palette — canonical dotbox brand hex values
// ============================================================

export const PALETTE = Object.freeze({
  surface: '#0A1024',      // page background
  panel: '#0F1730',        // cards / panels
  panelMuted: '#131C3A',   // subtler secondary panel
  border: '#1F2A4D',
  borderStrong: '#2A3860',

  accent: '#DC2626',       // primary CTA red
  accentHover: '#B91C1C',
  accentSoft: '#F87171',

  slate100: '#F1F5F9',
  slate300: '#CBD5E1',
  slate400: '#94A3B8',
  slate500: '#64748B',
  slate600: '#475569',
});

// ============================================================
// Colors — Tailwind classname form (drop into `className`)
// ============================================================

export const COLOR = Object.freeze({
  surface: 'bg-[#0A1024]',
  panel: 'bg-[#0F1730]',
  panelMuted: 'bg-[#131C3A]',
  panelSubtle: 'bg-[#0F1730]/60',

  border: 'border-[#1F2A4D]',
  borderStrong: 'border-[#2A3860]',

  accentBg: 'bg-[#DC2626]',
  accentHover: 'hover:bg-[#B91C1C]',
  accentText: 'text-rose-400',
  accentTextSoft: 'text-rose-300',
  accentBorder: 'border-rose-900/50',

  textPrimary: 'text-slate-100',
  textSecondary: 'text-slate-300',
  textMuted: 'text-slate-400',
  textFaint: 'text-slate-500',
});

// ============================================================
// Typography scale — canonical text sizes for the platform
// ============================================================

export const TEXT = Object.freeze({
  // Page title (matches DataSourcesPage baseline, 24px)
  h1: 'text-2xl font-bold text-slate-100',
  // Section heading within a page
  h2: 'text-lg font-semibold text-slate-100',
  // Subsection / card heading
  h3: 'text-sm font-semibold text-slate-100',
  // Body paragraph
  body: 'text-sm text-slate-300',
  // Page subtitle (renders under an h1)
  subtitle: 'text-sm text-slate-300',
  // Small caption
  small: 'text-xs text-slate-400',
  // Muted micro-text
  muted: 'text-xs text-slate-500',
  // Uppercase eyebrow label
  label: 'text-[10px] uppercase tracking-wider text-slate-500 font-semibold',
});

// ============================================================
// Layout / spacing scales
// ============================================================

export const LAYOUT = Object.freeze({
  // Standard dashboard page wrapper
  pageContainer: 'p-4 md:p-6 max-w-7xl mx-auto',
  // Wide dashboard page (data-heavy, e.g. DataSources tables)
  wideContainer: 'p-6 max-w-[1700px] mx-auto',
  // Medium dashboard page (max-w-7xl, kept for future opt-in use — no adopters currently)
  mediumContainer: 'p-4 md:p-6 max-w-7xl mx-auto',
  // Narrow reading page (settings, profile, search)
  narrowContainer: 'p-4 md:p-6 max-w-4xl mx-auto',

  // Vertical rhythm
  sectionGap: 'space-y-6',
  cardGap: 'space-y-4',
  tightGap: 'space-y-2',

  // Card interior padding
  cardPad: 'p-4',
  cardPadTight: 'p-3',
  cardPadRoomy: 'p-6',

  // Page-header trailing spacing
  headerBottomSpacing: 'mb-6',
});

// ============================================================
// Composed patterns — drop-in classname strings for common shapes
// ============================================================

export const PATTERN = Object.freeze({
  // Standard card
  card: 'bg-[#0F1730] border border-[#1F2A4D] rounded-lg p-4',
  cardMuted: 'bg-[#0F1730]/60 border border-[#1F2A4D] rounded-lg p-4',

  // Form inputs
  input: 'bg-[#0F1730] border-[#1F2A4D] text-slate-100',

  // Buttons (use variants sparingly — shadcn `<Button>` is the default)
  buttonGhost: 'border-[#1F2A4D] bg-[#0A1024] text-slate-200',
  buttonPrimary: 'bg-[#DC2626] hover:bg-[#B91C1C] text-white',

  // Chips / badges
  chip: 'text-[10px] bg-[#131C3A] text-slate-300 border-slate-700',
  chipMuted: 'text-[10px] bg-[#131C3A] text-slate-400 border-slate-700',
  chipAccent: 'text-[10px] bg-rose-950 text-rose-300 border-rose-900/50',
});

// Bundle for convenience
export const TOKENS = Object.freeze({
  PALETTE, COLOR, TEXT, LAYOUT, PATTERN,
});

// ============================================================
// iter68 — Canonical top-nav items (single source of truth)
// ============================================================
// Both the desktop `<Tabs>` and mobile scroller in DashboardLayout consume
// this array via `<NavigationTabs />`. If you need to add / rename / reorder
// a top-nav tab, do it HERE — it will propagate to both variants
// automatically. Do NOT hardcode tab lists anywhere else.
//
// Contract per item:
//   value    — URL segment (used by DashboardLayout.getCurrentTab / handleTabClick)
//   path     — full route path
//   labelKey — i18n key resolved by `t(labelKey)` in the component
// iter151 — "Data Sources" and "Feeds" were promoted to SUB-tabs of the
// Content Stream page (see components/dashboard/OverviewPage.jsx). The
// top-level Content Stream tab (value: 'overview') now hosts three
// sub-tabs: Connected Sources (default) / Data Sources / Feeds. The
// standalone `/dashboard/data-sources` and `/dashboard/feeds` routes are
// preserved for deep-linking + keyboard shortcuts (`g d`) — they render
// OverviewPage with the corresponding sub-tab pre-selected.
export const NAV_ITEMS = Object.freeze([
  Object.freeze({ value: 'command-center', path: '/dashboard/command-center',  labelKey: 'nav.commandCenter' }),
  Object.freeze({ value: 'overview',     path: '/dashboard',                labelKey: 'nav.overview' }),
  Object.freeze({ value: 'signals',      path: '/dashboard/signals',        labelKey: 'nav.signals' }),
  Object.freeze({ value: 'connections',  path: '/dashboard/connections',    labelKey: 'nav.connections' }),
  Object.freeze({ value: 'economist',    path: '/dashboard/economist',      labelKey: 'nav.economist' }),
  Object.freeze({ value: 'ai-agents',    path: '/dashboard/ai-agents',      labelKey: 'nav.aiAgents' }),
  Object.freeze({ value: 'decision-lab', path: '/dashboard/decision-lab',   labelKey: 'nav.decisionLab' }),
  Object.freeze({ value: 'social-ads-ai',path: '/dashboard/social-ads-ai',  labelKey: 'nav.socialAdsAi' }),
  // iter247 — `integrations` + `apis` moved OUT of the top nav and into the
  // left sidebar rail (see DEFAULT_SIDEBAR in DashboardLayout.jsx). Their
  // `nav.integrations` / `nav.apis` i18n keys are still used, just read by
  // the sidebar now. Do not re-add them here.
]);

export default TOKENS;
